import { useState, useEffect } from 'react';
import { monthsAPI, getErrorMessage } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/utils';
import { 
  CalendarCheck, 
  Lock, 
  Unlock, 
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

export default function MonthClosing() {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  
  const MONTHS = [
    t('months.january'), t('months.february'), t('months.march'), t('months.april'), 
    t('months.may'), t('months.june'), t('months.july'), t('months.august'),
    t('months.september'), t('months.october'), t('months.november'), t('months.december')
  ];
  
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { month, action: 'close'|'reopen' }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadMonths();
  }, [selectedYear]);

  const loadMonths = async () => {
    setLoading(true);
    try {
      const res = await monthsAPI.status(selectedYear);
      setMonths(res.data);
    } catch (error) {
      console.error('Failed to load months:', error);
      toast.error(t('months.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const closeMonth = async (month) => {
    setConfirmDialog({ month, action: 'close' });
  };

  const reopenMonth = async (month) => {
    setConfirmDialog({ month, action: 'reopen' });
  };

  const handleConfirm = async () => {
    if (!confirmDialog) return;
    const { month, action } = confirmDialog;
    setConfirmDialog(null);
    setActionLoading(month);
    try {
      if (action === 'close') {
        await monthsAPI.close(selectedYear, month);
        toast.success(`${MONTHS[month - 1]} ${selectedYear} ${t('months.closed').toLowerCase()}`);
      } else {
        await monthsAPI.reopen(selectedYear, month);
        toast.success(`${MONTHS[month - 1]} ${selectedYear} ${t('months.open').toLowerCase()}`);
      }
      loadMonths();
    } catch (error) {
      toast.error(getErrorMessage(error, t('months.closeError')));
    } finally {
      setActionLoading(null);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-500" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="month-closing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white font-heading flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-wp-medium/20 flex items-center justify-center">
              <CalendarCheck className="h-6 w-6 text-wp-light" />
            </div>
            {t('months.title')}
          </h1>
          <p className="text-wp-light/40 mt-2">{t('months.subtitle')}</p>
        </div>

        <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
          <SelectTrigger className="w-[120px] bg-wp-dark/30 border-wp-dark/50 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(year => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Months Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {months.map((m) => (
          <div 
            key={m.month} 
            className={`glass-card rounded-2xl p-6 hover-lift transition-all ${m.is_closed ? 'border-l-4 border-emerald-500 bg-emerald-500/5' : ''}`}
            data-testid={`month-card-${m.month}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${m.is_closed ? 'bg-emerald-500/20' : 'bg-wp-dark/50'}`}>
                  <CalendarCheck className={`h-5 w-5 ${m.is_closed ? 'text-emerald-400' : 'text-wp-light/40'}`} />
                </div>
                <h3 className="font-semibold text-white font-heading">{MONTHS[m.month - 1]}</h3>
              </div>
              {m.is_closed ? (
                <CheckCircle className="h-5 w-5 text-emerald-400" />
              ) : (
                <XCircle className="h-5 w-5 text-white/20" />
              )}
            </div>
            
            <div className="mb-4">
              <span className={`text-xs px-2.5 py-1 rounded-full ${m.is_closed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                {m.is_closed ? t('months.closed') : t('months.open')}
              </span>
            </div>
            
            {m.closed_at && (
              <p className="text-xs text-wp-light/40 mb-4">
                {t('months.closedAt')}: {formatDateTime(m.closed_at)}
              </p>
            )}

            <div>
              {m.is_closed ? (
                isAdmin && (
                  <button
                    onClick={() => reopenMonth(m.month)}
                    disabled={actionLoading === m.month}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-white rounded-xl border border-wp-dark/50 transition-colors disabled:opacity-50"
                    data-testid={`reopen-month-${m.month}`}
                  >
                    {actionLoading === m.month ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Unlock className="h-4 w-4" />
                        {t('months.reopen')}
                      </>
                    )}
                  </button>
                )
              ) : (
                <button
                  onClick={() => closeMonth(m.month)}
                  disabled={actionLoading === m.month}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-wp-medium hover:bg-wp-medium text-white font-medium rounded-xl transition-all btn-glow disabled:opacity-50"
                  data-testid={`close-month-${m.month}`}
                >
                  {actionLoading === m.month ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      {t('months.closeMonth')}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={(open) => !open && setConfirmDialog(null)}>
        <DialogContent className="bg-wp-dark border-wp-dark/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              {confirmDialog?.action === 'close' ? t('months.confirmCloseTitle') || 'Close month?' : t('months.confirmReopenTitle') || 'Reopen month?'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-wp-light/70 text-sm">
              {confirmDialog?.action === 'close'
                ? (t('months.confirmCloseBody') || 'After closing, employees will not be able to edit hours for this period. You can reopen the month at any time.')
                : (t('months.confirmReopenBody') || 'The month will be reopened and employees will be able to edit their hours again.')
              }
            </p>
            <p className="text-white font-medium">
              {confirmDialog && `${MONTHS[confirmDialog.month - 1]} ${selectedYear}`}
            </p>
            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setConfirmDialog(null)} className="border-wp-dark/50 text-wp-light/70 hover:text-white">
                {t('common.cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleConfirm}
                className={confirmDialog?.action === 'close' ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}
              >
                {confirmDialog?.action === 'close' ? (t('months.closeMonth') || 'Close month') : (t('months.reopenMonth') || 'Reopen')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Info */}
      <div className="glass-card rounded-2xl p-6">
        <h3 className="font-semibold text-white mb-4 font-heading">{t('months.aboutTitle')}</h3>
        <ul className="text-sm text-wp-light/40 space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-wp-light">•</span>
            {t('months.aboutItem1')}
          </li>
          <li className="flex items-start gap-2">
            <span className="text-wp-light">•</span>
            {t('months.aboutItem2')}
          </li>
          <li className="flex items-start gap-2">
            <span className="text-wp-light">•</span>
            {t('months.aboutItem3')}
          </li>
          <li className="flex items-start gap-2">
            <span className="text-wp-light">•</span>
            {t('months.aboutItem4')}
          </li>
        </ul>
      </div>
    </div>
  );
}
